/**
 * <content-drawers>
 * -----------------
 * Controller for storewide lightweight info drawers.
 *
 * Responsibilities:
 *   1. Listen (via delegation) for clicks on any [data-drawer-trigger]
 *   2. On trigger, clone the matching <template> into the shared <dialog>
 *   3. Call dialog.showModal() — browser handles focus trap, Escape, inert
 *   4. Wire up drag-to-dismiss on mobile (pointer events)
 *   5. Close on backdrop click, close button, Escape, drag threshold
 *   6. Return focus to triggering element on close (WCAG 2.4.3)
 *
 * Accessibility:
 *   - Upgrades non-<button> triggers at runtime (role, tabindex, keyboard)
 *   - Respects prefers-reduced-motion (skips close animation)
 *   - Native <dialog> provides modal semantics, focus trap, inert background
 *   - Drag is enhancement only; every path has a keyboard equivalent
 */

(function () {
  'use strict';

  const DESKTOP_BREAKPOINT = 768;

  // Drag: close if pulled down > 25% of panel height, or velocity > 0.5 px/ms
  const DRAG_DISMISS_THRESHOLD = 0.25;
  const DRAG_DISMISS_VELOCITY = 0.5;

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const isDesktop = () => window.innerWidth >= DESKTOP_BREAKPOINT;

  class ContentDrawers extends HTMLElement {
    constructor() {
      super();
      this.dialog = null;
      this.panel = null;
      this.grabber = null;
      this.contentTarget = null;
      this.labelTarget = null;
      this.templates = new Map();
      this.lastTrigger = null;
      this.dragState = null;

      this._onDocClick = this._onDocClick.bind(this);
      this._onDocKeydown = this._onDocKeydown.bind(this);
      this._onDialogClick = this._onDialogClick.bind(this);
      this._onDialogClose = this._onDialogClose.bind(this);
      this._onDialogCancel = this._onDialogCancel.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._onDialogTouchMove = this._onDialogTouchMove.bind(this);
    }

    connectedCallback() {
      this.dialog = this.querySelector('[data-content-drawer]');
      if (!this.dialog) return;

      this.panel = this.dialog.querySelector('[data-drawer-panel]');
      this.grabber = this.dialog.querySelector('[data-drawer-grabber]');
      this.contentTarget = this.dialog.querySelector('[data-drawer-content]');
      this.labelTarget = this.dialog.querySelector('[data-drawer-label]');

      this.querySelectorAll('.content-drawer-template').forEach((tpl) => {
        const handle = tpl.getAttribute('data-drawer-handle');
        if (handle) this.templates.set(handle, tpl);
      });

      this._upgradeTriggers(document);

      document.addEventListener('click', this._onDocClick);
      document.addEventListener('keydown', this._onDocKeydown);

      this.dialog.addEventListener('click', this._onDialogClick);
      this.dialog.addEventListener('close', this._onDialogClose);
      this.dialog.addEventListener('cancel', this._onDialogCancel);
      // Non-passive so we can preventDefault on attempted page-through scroll.
      // Scoped to the dialog itself, so it only fires while the dialog is open.
      this.dialog.addEventListener('touchmove', this._onDialogTouchMove, { passive: false });

      if (this.panel) {
        this.panel.addEventListener('pointerdown', this._onPointerDown);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
      document.removeEventListener('keydown', this._onDocKeydown);
      if (this.dialog) {
        this.dialog.removeEventListener('click', this._onDialogClick);
        this.dialog.removeEventListener('close', this._onDialogClose);
        this.dialog.removeEventListener('cancel', this._onDialogCancel);
        this.dialog.removeEventListener('touchmove', this._onDialogTouchMove);
      }
    }

    /* --------------------------------------------------------------
       Trigger handling (event delegation — storewide)
       -------------------------------------------------------------- */

    _onDocClick(event) {
      const trigger = event.target.closest('[data-drawer-trigger]');
      if (!trigger) return;

      const handle = trigger.getAttribute('data-drawer-trigger');
      if (!handle || !this.templates.has(handle)) return;

      event.preventDefault();
      this.open(handle, trigger);
    }

    _onDocKeydown(event) {
      // Keyboard activation for non-button triggers (native buttons handle this themselves)
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const trigger = event.target.closest('[data-drawer-trigger]');
      if (!trigger || trigger.tagName === 'BUTTON') return;

      const handle = trigger.getAttribute('data-drawer-trigger');
      if (!handle || !this.templates.has(handle)) return;

      event.preventDefault();
      this.open(handle, trigger);
    }

    _upgradeTriggers(root) {
      const triggers = root.querySelectorAll(
        '[data-drawer-trigger]:not(button):not([data-drawer-upgraded])'
      );
      triggers.forEach((el) => {
        el.setAttribute('data-drawer-upgraded', '');
        // <a> with href is already focusable/keyboard-activatable — leave alone
        if (el.tagName === 'A' && el.hasAttribute('href')) return;
        if (!el.hasAttribute('role')) el.setAttribute('role', 'button');
        if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
      });
    }

    /* --------------------------------------------------------------
       Open / close
       -------------------------------------------------------------- */

    open(handle, trigger) {
      const tpl = this.templates.get(handle);
      if (!tpl || !this.dialog) return;

      this.lastTrigger = trigger || document.activeElement;

      // Update accessible name (aria-labelledby points at labelTarget — stable reference)
      const headline = tpl.getAttribute('data-drawer-headline') || '';
      if (this.labelTarget) this.labelTarget.textContent = headline;

      // Swap content
      if (this.contentTarget) {
        this.contentTarget.replaceChildren();
        this.contentTarget.appendChild(tpl.content.cloneNode(true));
      }

      // Reset state
      this.panel.style.setProperty('--drag-y', '0px');
      this.dialog.removeAttribute('data-closing');
      this.dialog.removeAttribute('data-dragging');

      try {
        this.dialog.showModal();
      } catch (_err) {
        this.dialog.close();
        this.dialog.showModal();
      }
    }

    close() {
      if (!this.dialog || !this.dialog.open) return;

      if (prefersReducedMotion()) {
        this.dialog.close();
        return;
      }

      this.dialog.setAttribute('data-closing', 'true');
      const onEnd = (event) => {
        // animationend bubbles — ensure we only react to the panel's own animation,
        // not an animation on a descendant (e.g. if merchant adds animated content)
        if (event && event.target !== this.panel) return;
        this.panel.removeEventListener('animationend', onEnd);
        // Close FIRST, then remove the closing attribute. If we removed the
        // attribute first, the browser would paint one frame of the panel at
        // its resting (fully-visible) position before `close()` hides the
        // dialog — causing a visible pop.
        this.dialog.close();
        this.dialog.removeAttribute('data-closing');
      };
      this.panel.addEventListener('animationend', onEnd);

      // Safety net in case animationend doesn't fire (e.g. tab backgrounded)
      setTimeout(() => {
        if (this.dialog.hasAttribute('data-closing')) onEnd();
      }, 300);
    }

    _onDialogClick(event) {
      if (event.target.closest('[data-drawer-close]')) {
        this.close();
        return;
      }
      // Backdrop click — the click lands on the <dialog> itself, not a child
      if (event.target === this.dialog) {
        this.close();
      }
    }

    _onDialogClose() {
      this.panel.style.setProperty('--drag-y', '0px');
      this.dialog.removeAttribute('data-dragging');

      // Focus return — WCAG 2.4.3
      if (this.lastTrigger && typeof this.lastTrigger.focus === 'function') {
        try {
          this.lastTrigger.focus({ preventScroll: true });
        } catch (_) {
          this.lastTrigger.focus();
        }
      }
      this.lastTrigger = null;

      // Clear content after close completes (frees memory, prevents stale content flash on next open)
      if (this.contentTarget) this.contentTarget.replaceChildren();
      if (this.labelTarget) this.labelTarget.textContent = '';
    }

    _onDialogCancel(event) {
      // Escape fires "cancel". Intercept to run our close animation for consistency.
      if (prefersReducedMotion()) return;
      event.preventDefault();
      this.close();
    }

    /* --------------------------------------------------------------
       Drag to dismiss
       --------------------------------------------------------------
       Implementation notes:
       - We listen for pointermove/pointerup on `window` (not the panel).
         This is critical: <dialog> + showModal() creates a top-layer
         context that can interfere with setPointerCapture on the panel,
         causing mid-drag event loss. Window listeners are bulletproof.
       - touch-action: none in CSS is what actually lets us receive the
         vertical gesture events (instead of the browser claiming them
         for native scroll).
       - We only engage drag once the user has moved >5px AND vertical
         movement dominates. This prevents hijacking horizontal taps
         and tiny wobbles.
    */

    _onPointerDown(event) {
      // Only react on mobile-ish viewports. Use (pointer: coarse) so we
      // don't bail on narrow desktop dev-tools views — but do bail on
      // actual desktop with a real mouse (where drag would feel weird).
      if (isDesktop() && !window.matchMedia('(pointer: coarse)').matches) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      // Don't start drag on interactive elements
      if (event.target.closest('a, button, [role="button"]')) return;

      const panelRect = this.panel.getBoundingClientRect();
      this.dragState = {
        startY: event.clientY,
        startX: event.clientX,
        startTime: performance.now(),
        lastY: event.clientY,
        lastTime: performance.now(),
        panelHeight: panelRect.height,
        pointerId: event.pointerId,
        // We haven't committed to a drag yet — waiting for intent
        committed: false,
      };

      // Listen on window, not the panel — reliable across <dialog> top-layer quirks
      window.addEventListener('pointermove', this._onPointerMove, { passive: false });
      window.addEventListener('pointerup', this._onPointerUp);
      window.addEventListener('pointercancel', this._onPointerUp);
    }

    _onPointerMove(event) {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

      const dy = event.clientY - this.dragState.startY;
      const dx = event.clientX - this.dragState.startX;

      // Commit to the drag only when we see clear vertical intent.
      // This avoids hijacking tiny taps and horizontal swipes.
      if (!this.dragState.committed) {
        const moved = Math.hypot(dx, dy);
        if (moved < 5) return;
        // If motion is more horizontal than vertical, abandon — user isn't dragging the sheet
        if (Math.abs(dx) > Math.abs(dy)) {
          this._endDrag(false);
          return;
        }
        this.dragState.committed = true;
        this.dialog.setAttribute('data-dragging', 'true');
      }

      // Prevent the browser from doing anything else with this gesture
      // (e.g. text selection, image drag, pull-to-refresh)
      if (event.cancelable) event.preventDefault();

      // Only allow downward drag; small upward resistance for rubber-band feel
      const translated = dy > 0 ? dy : dy * 0.2;
      this.panel.style.setProperty('--drag-y', `${translated}px`);

      this.dragState.lastY = event.clientY;
      this.dragState.lastTime = performance.now();
    }

    _onPointerUp(event) {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

      const committed = this.dragState.committed;
      const { startY, startTime, lastY, lastTime, panelHeight } = this.dragState;

      this._endDrag(committed);

      if (!committed) return; // never committed to a drag — nothing to dismiss

      const totalDy = lastY - startY;
      const elapsed = Math.max(1, lastTime - startTime);
      const velocity = totalDy / elapsed;

      const dismissByDistance = totalDy > panelHeight * DRAG_DISMISS_THRESHOLD;
      const dismissByVelocity = velocity > DRAG_DISMISS_VELOCITY && totalDy > 20;

      if (dismissByDistance || dismissByVelocity) {
        this.close();
      } else {
        this.panel.style.setProperty('--drag-y', '0px');
      }
    }

    _endDrag(wasCommitted) {
      window.removeEventListener('pointermove', this._onPointerMove);
      window.removeEventListener('pointerup', this._onPointerUp);
      window.removeEventListener('pointercancel', this._onPointerUp);

      this.dragState = null;
      this.dialog.removeAttribute('data-dragging');

      if (!wasCommitted) {
        // Make sure we don't leave any partial offset behind
        this.panel.style.setProperty('--drag-y', '0px');
      }
    }

    /* --------------------------------------------------------------
       Scroll-through prevention
       --------------------------------------------------------------
       We intentionally do NOT mutate html/body styles (no overflow:hidden,
       no position:fixed). Every technique that touches those properties
       causes visible side effects on iOS Safari:
         - position:fixed evicts paint tiles → flicker on close
         - overflow:hidden triggers URL-bar state re-evaluation → page
           shifts down ~50px on open and back up on close
         - both can break sticky/fixed headers

       Instead: we let the native <dialog> + showModal() provide inert
       background handling (which blocks clicks, tab focus, and keyboard
       interaction with background elements automatically), and we add a
       narrow touchmove listener on the dialog to prevent finger-drag scroll
       from reaching the page behind.

       The touchmove handler:
         - Allows drag-to-dismiss on the panel (we're already handling those
           events via pointerdown/move/up — don't block them)
         - Blocks any touch-scroll attempt over the backdrop
         - Does NOT block scroll within the drawer content (there isn't any
           by design, but if a merchant ever adds scrollable content, this
           wouldn't interfere with it)
    */

    _onDialogTouchMove(event) {
      if (!this.dialog || !this.dialog.open) return;

      // If the touch is on the panel, let our pointer handlers manage it.
      // If the touch is on the backdrop (i.e. event.target is the dialog),
      // block it so the page behind doesn't scroll.
      if (event.target === this.dialog) {
        if (event.cancelable) event.preventDefault();
      }
      // Note: we deliberately do NOT prevent on panel touches. If there were
      // scrollable content inside the drawer in the future, it would need to
      // scroll freely. Our drag-to-dismiss pointer handlers already call
      // preventDefault() on their own committed moves, which is the right scope.
    }
  }

  if (!customElements.get('content-drawers')) {
    customElements.define('content-drawers', ContentDrawers);
  }
})();